import { useState } from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { WarningIcon } from 'assets/icons';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { Dropdown } from 'components/Forms';
import { ToastNotificationContent } from 'components/UI';

const shippingProviderOptions: ISelectOption[] = [
  { label: 'SVD', value: 'SVD' },
  { label: 'Amazon', value: 'Amazon' },
  { label: 'Smart Meter', value: 'Smart Meter' }
];

interface IExportPendingOrdersModalProps {
  onClose: () => void;
}

const ExportPendingOrdersModal = ({ onClose }: IExportPendingOrdersModalProps) => {
  const { formatMessage } = useIntl();
  const [selectedProvider, setSelectedProvider] = useState<ISelectOption | null>(null);

  const [{ loading, error }, exportOrders] = useAxios<{ url?: string }>(
    {
      url: '/clinician-gateway/report/device-orders',
      method: 'GET'
    },
    { manual: true }
  );

  const handleExport = () => {
    const params = selectedProvider
      ? { shippingProvider: selectedProvider.value as ShippingProvider }
      : undefined;

    exportOrders({ params })
      .then((response) => {
        const url = response.data?.url;
        if (!url) return Promise.reject();
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        onClose();
      })
      .catch(() => {
        toast.error(
          <ToastNotificationContent
            icon={<WarningIcon className="icon" />}
            text="downloadPendingOrdersErrorKey"
          />
        );
      });
  };

  return (
    <Modal show onHide={onClose} data-test="export-pending-orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>{formatMessage({ id: 'downloadPendingOrdersKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Loading />
        ) : (
          <>
            {error && (
              <Error errorMessage={formatMessage({ id: 'downloadPendingOrdersErrorKey' })} />
            )}
            <Dropdown
              label={formatMessage({ id: 'shippingProviderKey' })}
              options={shippingProviderOptions}
              value={selectedProvider}
              onChange={(option: ISelectOption | null) => setSelectedProvider(option)}
              isClearable
              variant="form"
              dataTestId="shipping-provider-select"
              placeholder={formatMessage({ id: 'selectShippingProviderKey' })}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onClose} disabled={loading}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={loading}
          data-test="export-pending-orders-btn"
        >
          {formatMessage({ id: 'downloadPendingOrdersKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { ExportPendingOrdersModal };
